"use client";

import { getIsRegScreenSkipable } from "@/helpers/getIsRegScreenSkipable";
import { shouldShowBoardScreen } from "@/helpers/getShouldShowBoardScreen";
import { useCallback } from "react";

import {
  EXPERIENCED_USER_SCREEN,
  FRESHER_USER_SCREEN,
  RegMapKeyType,
  regScreen,
} from "@/components/registration/map";

/* -------------------- helpers -------------------- */

type FlowKey = (typeof regScreen)[number];

const getFlow = (isExperienced: boolean): Partial<RegMapKeyType>[] =>
  isExperienced ? EXPERIENCED_USER_SCREEN : FRESHER_USER_SCREEN;

const getDefaultNextScreen = (
  flow: Partial<RegMapKeyType>[],
  currentScreen: RegMapKeyType,
): RegMapKeyType => {
  const index = flow.indexOf(currentScreen);
  return index === -1 ? flow[0] : (flow[index + 1] ?? flow[index]);
};

/* -------------------- rule engine -------------------- */

type RuleContext = {
  currentScreen: RegMapKeyType;
  nextScreen: RegMapKeyType;
  orderedScreens: Partial<RegMapKeyType>[];
  userData: any;
  isExperienced: boolean;
  siteContext: string;
  isTriumph: boolean;
};

type ScreenRule = (ctx: RuleContext) => RegMapKeyType | null;

/**
 * Returns true if the screen already has meaningful data in userData
 * (i.e. screen can be safely skipped)
 */
const screenDataCheckers: Partial<Record<RegMapKeyType, ScreenDataChecker>> = {
  MOBILE_NUMBER_EMAIL: (userData) =>
    Boolean(
      userData?.personalDetailSection?.personalDetails?.emails?.[0]?.email ||
      userData?.personalDetailSection?.personalDetails?.mobileNumbers?.[0]
        ?.mobileNumber,
    ),

  LINKEDIN: (userData) =>
    Boolean(
      userData?.socialPresenceSection?.presences?.some(
        (p: any) => p.name === "Linkedin" && p.url,
      ),
    ),

  FULL_NAME: (userData) =>
    Boolean(userData?.personalDetailSection?.personalDetails?.fullName),

  TOTAL_EXPERIENCE: (userData) =>
    userData?.personalDetailSection?.personalDetails?.additionalPersonalDetail
      ?.experience != null,

  CURRENT_LOCATION: (userData) =>
    Boolean(
      userData?.personalDetailSection?.personalDetails?.additionalPersonalDetail
        ?.currentLocation?.id,
    ),

  NATIONALITY: (userData, siteContext) => {
    const currentLocationCountry =
      userData?.personalDetailSection?.personalDetails?.additionalPersonalDetail
        ?.currentLocation?.extraInfo?.countryName;

    return (
      (siteContext === "rexmonster" && currentLocationCountry === "India") ||
      Boolean(userData?.personalDetailSection?.personalDetails?.nationality?.id)
    );
  },

  WORK_AUTH: (userData, siteContext) => {
    const currentLocationCountry =
      userData?.personalDetailSection?.personalDetails?.additionalPersonalDetail
        ?.currentLocation?.extraInfo?.countryName;

    return (
      (siteContext === "rexmonster" && currentLocationCountry === "India") ||
      Boolean(
        userData?.personalDetailSection?.personalDetails
          ?.additionalPersonalDetail?.workVisaType?.id,
      )
    );
  },

  CURRENT_DESIGNATION_CURRENT_COMPANY: (userData) =>
    Boolean(
      userData?.employmentDetailSection?.employments?.[0]?.designation?.id,
    ),

  START_DATE_CURRENTLY_WORKING_HERE_CHECKBOX: (userData) =>
    Boolean(userData?.employmentDetailSection?.employments?.[0]?.startDate),

  END_DATE: (userData) =>
    Boolean(
      userData?.employmentDetailSection?.employments?.[0]?.endDate ||
      userData?.employmentDetailSection?.employments?.[0]?.endDate === null,
    ),

  NOTICE_PERIOD: (userData) =>
    userData?.employmentDetailSection?.employments?.[0]?.noticePeriod?.days !=
    null,

  LAST_WORKING_DAY: (userData) =>
    Boolean(
      userData?.employmentDetailSection?.employments?.[0]?.lastWorkingDay,
    ),

  CURRENT_SALARY_HIDE_SALARY_CHECKBOX: (userData) =>
    userData?.employmentDetailSection?.employments?.[0]?.salary
      ?.absoluteValue != null,

  SKILLS: (userData) =>
    Array.isArray(userData?.skillSection?.skills) &&
    userData.skillSection.skills.length > 0,

  INDUSTRY: (userData) =>
    Boolean(
      userData?.employmentDetailSection?.employments?.[0]?.industries?.id,
    ),

  GENDER: (userData) =>
    Boolean(
      userData?.personalDetailSection?.personalDetails?.additionalPersonalDetail
        ?.gender?.id,
    ),

  TRIUMPH_FIELDS: (userData, siteContext, isTriumph) =>
    !isTriumph ||
    Boolean(
      userData?.personalDetailSection?.personalDetails?.additionalPersonalDetail
        ?.triumphFields,
    ),

  HIGHEST_QUALIFICATION: (userData) =>
    Boolean(
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.id,
    ),

  // Skip only if specialisation id is present or highest qualification is 10th or 12th class
  SPECIALISATION: (userData) =>
    Boolean(
      userData?.educationDetailSection?.educations?.[0]?.specialization?.id,
    ) ||
    Boolean(
      userData?.educationDetailSection?.educations?.[0]?.specialization?.name,
    ) ||
    shouldShowBoardScreen(
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.id,
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.text,
    ),

  // Skip only if college id is present or highest qualification is 10th or 12th class
  UNIVERSITY: (userData) =>
    Boolean(userData?.educationDetailSection?.educations?.[0]?.college?.id) ||
    Boolean(userData?.educationDetailSection?.educations?.[0]?.college?.name) ||
    shouldShowBoardScreen(
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.id,
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.text,
    ),

  // Skip only if board id is present or highest qualification is not 10th or 12th class
  QUALIFICATION_BOARD: (userData) =>
    Boolean(userData?.educationDetailSection?.educations?.[0]?.board?.id) ||
    Boolean(userData?.educationDetailSection?.educations?.[0]?.board?.name) ||
    !shouldShowBoardScreen(
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.id,
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.text,
    ),

  // Skip only if medium id is present or highest qualification is not 10th or 12th class
  QUALIFICATION_MEDIUM: (userData) =>
    Boolean(userData?.educationDetailSection?.educations?.[0]?.medium?.id) ||
    Boolean(userData?.educationDetailSection?.educations?.[0]?.medium?.name) ||
    !shouldShowBoardScreen(
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.id,
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.text,
    ),

  YEAR_OF_GRADUATION: (userData) =>
    Boolean(userData?.educationDetailSection?.educations?.[0]?.yearOfPassing),

  EDUCATION_TYPE: (userData) =>
    Boolean(
      userData?.educationDetailSection?.educations?.[0]?.educationType?.id,
    ),

  PREFERRED_JOB_TITLE: (userData) =>
    Array.isArray(userData?.jobPreferenceSection?.jobPreference?.roles) &&
    userData.jobPreferenceSection.jobPreference.roles.length > 0,

  PREFERRED_LOCATION: (userData) =>
    Array.isArray(userData?.jobPreferenceSection?.jobPreference?.locations) &&
    userData.jobPreferenceSection.jobPreference.locations.length > 0,

  WHAT_ARE_YOU_LOOKING_FOR_CHECKBOX: () => true,
};

const hasDataForScreen = (
  screen: RegMapKeyType,
  userData: any,
  siteContext: string,
  isTriumph?: boolean,
): boolean => {
  const checker = screenDataCheckers[screen];
  return checker ? checker(userData, siteContext, isTriumph) : false;
};

const autoSkipFilledScreensRule: ScreenRule = ({
  nextScreen,
  orderedScreens,
  userData,
  siteContext,
  isTriumph,
}) => {
  let index = orderedScreens.indexOf(nextScreen);

  while (
    index < orderedScreens.length &&
    hasDataForScreen(orderedScreens[index], userData, siteContext, isTriumph)
  ) {
    index++;
  }

  const finalScreen = orderedScreens[index] ?? null;

  return finalScreen;
};

const rules: ScreenRule[] = [
  ({ currentScreen, siteContext }) => {
    if (currentScreen !== "MOBILE_NUMBER_EMAIL") return null;

    return siteContext === "rexmonster" ? "MOBILE_OTP" : "EMAIL_OTP";
  },

  ({ currentScreen, siteContext }) => {
    if (siteContext === "rexmonster" && currentScreen === "MOBILE_OTP") {
      return "RESUME";
    }

    if (siteContext !== "rexmonster" && currentScreen === "EMAIL_OTP") {
      return "RESUME";
    }
    return null;
  },

  ({ currentScreen, userData, isExperienced }) => {
    if (currentScreen !== "CURRENT_LOCATION") return null;

    const country =
      userData?.personalDetailSection?.personalDetails?.additionalPersonalDetail
        ?.currentLocation?.extraInfo?.countryName;

    return country !== "India"
      ? "NATIONALITY"
      : isExperienced
        ? "CURRENT_DESIGNATION_CURRENT_COMPANY"
        : "PREFERRED_JOB_TITLE";
  },

  ({ currentScreen, userData, isExperienced }) => {
    if (currentScreen !== "NATIONALITY") return null;

    const locationCountry =
      userData?.personalDetailSection?.personalDetails?.additionalPersonalDetail
        ?.currentLocation?.extraInfo?.countryUuId;

    const nationalityCountry =
      userData?.personalDetailSection?.personalDetails?.nationality?.extraInfo
        ?.countryUuId;

    return locationCountry !== nationalityCountry
      ? "WORK_AUTH"
      : isExperienced
        ? "CURRENT_DESIGNATION_CURRENT_COMPANY"
        : "PREFERRED_JOB_TITLE";
  },

  ({ currentScreen, userData }) => {
    if (currentScreen !== "START_DATE_CURRENTLY_WORKING_HERE_CHECKBOX")
      return null;

    const employment = userData?.employmentDetailSection?.employments?.[0];
    return employment?.current ? "SKILLS" : null;
  },

  ({ currentScreen, userData }) => {
    if (currentScreen !== "CURRENT_SALARY_HIDE_SALARY_CHECKBOX") return null;

    const employment = userData?.employmentDetailSection?.employments?.[0];
    return !employment?.endDate ? "NOTICE_PERIOD" : "LINKEDIN";
  },

  ({ currentScreen, userData }) => {
    if (currentScreen !== "NOTICE_PERIOD") return null;

    const employment = userData?.employmentDetailSection?.employments?.[0];
    return employment?.noticePeriod?.serving === false ? "LINKEDIN" : null;
  },

  ({ currentScreen, userData }) => {
    if (currentScreen !== "HIGHEST_QUALIFICATION") return null;

    const qualificationText =
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.text;

    const qualificationId =
      userData?.educationDetailSection?.educations?.[0]?.highestQualification
        ?.id;

    const shouldShowBoardFields = shouldShowBoardScreen(
      qualificationId,
      qualificationText,
    );

    return shouldShowBoardFields ? "QUALIFICATION_BOARD" : null;
  },

  ({ currentScreen }) =>
    currentScreen === "UNIVERSITY" ? "YEAR_OF_GRADUATION" : null,

  ({ nextScreen, orderedScreens, siteContext }) => {
    if (siteContext === "monstersingapore" && nextScreen === "GENDER") {
      return orderedScreens[orderedScreens.indexOf(nextScreen) + 1];
    }
    return null;
  },

  ({ nextScreen, orderedScreens, isTriumph }) => {
    if (!isTriumph && nextScreen === "TRIUMPH_FIELDS") {
      return orderedScreens[orderedScreens.indexOf(nextScreen) + 1];
    }
    return null;
  },

  ({ nextScreen, orderedScreens, userData }) => {
    if (nextScreen !== "WHAT_ARE_YOU_LOOKING_FOR_CHECKBOX") {
      return null;
    }

    const yearOfPassing =
      userData?.educationDetailSection?.educations?.[0]?.yearOfPassing;

    if (!yearOfPassing) return null;

    const inputDate = new Date(`${yearOfPassing}-03-01`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // If yearOfPassing is NOT in the future → skip this screen
    if (inputDate <= today) {
      const index = orderedScreens.indexOf(nextScreen);
      return orderedScreens[index + 1] ?? null;
    }

    return null;
  },

  // check if data is already present for a particular screen
  // and if the data is present skip to next screen
  autoSkipFilledScreensRule,
];

type ScreenDataChecker = (
  userData: any,
  siteContext: string,
  isTriumph?: boolean,
) => boolean;

const applyRules = (ctx: RuleContext): RegMapKeyType => {
  let currentNext = ctx.nextScreen;

  for (const rule of rules.filter((r) => r !== autoSkipFilledScreensRule)) {
    const result = rule({ ...ctx, nextScreen: currentNext });
    if (result && result !== currentNext) {
      currentNext = result;
    }
  }

  return (
    autoSkipFilledScreensRule({
      ...ctx,
      nextScreen: currentNext,
    }) ?? currentNext
  );
};

/* -------------------- hook -------------------- */

type GetNextScreenProps = {
  currentScreen: RegMapKeyType;
  userData: any;
  siteContext: string;
  isTriumph: boolean;
};

type GetNextScreenReturnType = {
  nextScreen: RegMapKeyType;
  profileProgress: number;
  isSkipable: boolean;
};

const useNextRegScreen = () => {
  const getNextScreenFn = ({
    currentScreen,
    userData,
    siteContext,
    isTriumph,
  }: GetNextScreenProps): GetNextScreenReturnType => {
    const isExperienced =
      userData?.personalDetailSection?.personalDetails?.experienceLevel !==
      "FRESHER";
    const flow = getFlow(isExperienced);

    const defaultNext = getDefaultNextScreen(flow, currentScreen);

    const nextScreen = applyRules({
      currentScreen,
      nextScreen: defaultNext,
      orderedScreens: flow,
      userData,
      isExperienced,
      siteContext,
      isTriumph,
    }) as FlowKey;

    const index = flow.indexOf(nextScreen);
    const profileProgress = Math.round(((index + 1) / flow.length) * 100);

    return {
      nextScreen,
      profileProgress,
      isSkipable: getIsRegScreenSkipable({
        siteMap: siteContext,
        isExperienced,
        screen: nextScreen,
      }),
    };
  };

  const getNextScreen = useCallback(getNextScreenFn, []);

  return { getNextScreen };
};

export default useNextRegScreen;
